import Select, {
  createFilter,
  components,
  OptionsOrGroups,
  GroupBase,
} from 'react-select';
import { useEffect, useState } from 'react';
import { CheckIcon } from '@radix-ui/react-icons';
import { useOrganizationContext } from '@/contexts/organization-context';
import { useRouter } from 'next/router';
import { RoutePaths } from '@/consts/routes';

type optionType = {
  name: string;
  id: string;
};

const customStyles = {
  control: (styles: any, { isFocused, menuIsOpen }: any) => ({
    ...styles,
    marginTop: '4px',
    maxWidth: '17.5rem',
    padding: '1px 8px',
    background: '#F9F9FA',
    borderRadius: '10px',
    borderColor: isFocused || menuIsOpen ? 'rgb(78 76 255)' : 'transparent',
    boxShadow: 'none',
    '&:hover': {
      ...styles['&:hover'],
      borderColor: menuIsOpen ? 'rgb(78 76 255)' : 'hsla(240, 1%, 63%, 1)',
    },
    cursor: 'pointer',
    borderWidth: '1px',
  }),
  input: (styles: any) => ({
    ...styles,
    color: '#7977A2',
  }),

  option: (styles: any, { isFocused, isSelected }: any) => ({
    ...styles,
    color: 'black',
    fontSize: 14,
    background: isFocused ? 'lightgray' : isSelected ? '#fff' : undefined,
    zIndex: 1,
    cursor: 'pointer',
    borderRadius: '4px',
    maxWidth: '96%',
    marginLeft: '8px',
  }),
};

const filterConfig = {
  ignoreCase: true,
  ignoreAccents: false,
  trim: false,
  matchFrom: 'start' as const,
};

export function OrganizationSelector({ onSelect }: { onSelect?: () => void }) {
  const { organizationId, setOrganizationId, organizationList } =
    useOrganizationContext();
  const [selectedOrgName, setSelectedOrgName] = useState('');
  const initialValue = { name: selectedOrgName, id: organizationId || '' };
  const router = useRouter();
  const isMaggiePage = router.pathname.includes(RoutePaths.MaggieHome);
  const isGrantPage = router.pathname.includes(RoutePaths.GrantHome);
  const displayList = organizationList?.sort((a, b) => {
    const nameA = (a?.name || '').toLowerCase();
    const nameB = (b?.name || '').toLowerCase();

    if (nameA && nameB) {
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
    }

    return 0;
  });

  const Option = (props: any) => (
    <components.Option {...props}>
      <div className='flex items-center justify-between'>
        <span>{props.label}</span>
        {props.data.id === organizationId && <CheckIcon />}
      </div>
    </components.Option>
  );

  useEffect(() => {
    if (organizationId) {
      const organization = organizationList?.find(
        (org) => org?.id === organizationId
      );

      if (organization) {
        setSelectedOrgName(organization.name || '');
      }
    }
  }, [organizationList, organizationId]);

  const handleRedirect = (organizationId?: string) => {
    if (location.pathname.includes(RoutePaths.Workspace)) {
      router.push(`${RoutePaths.Workspace}?organizationid=${organizationId}`);
    } else if (isMaggiePage) {
      router.push(`${RoutePaths.MaggieHome}?organizationid=${organizationId}`);
    } else if (isGrantPage) {
      router.push(`${RoutePaths.GrantHome}?organizationid=${organizationId}`);
    }
  };

  {
    /* TODO: Replace with Suspense */
  }

  if (organizationList && organizationList?.length <= 1) return null;

  return (
    <>
      <Select
        placeholder='Select Organization...'
        value={initialValue || null}
        components={{
          IndicatorSeparator: () => null,
          Option,
        }}
        isSearchable
        onChange={(currentValue) => {
          setOrganizationId &&
            currentValue?.id &&
            setOrganizationId(currentValue.id);
          handleRedirect(currentValue?.id);
          if (onSelect) {
            onSelect();
          }
        }}
        styles={customStyles}
        getOptionLabel={(option: optionType) => option?.name}
        options={
          displayList as
            | OptionsOrGroups<
                { name: string; id: string },
                GroupBase<{ name: string; id: string }>
              >
            | undefined
        }
        filterOption={createFilter(filterConfig)}
      />
    </>
  );
}
